"use client"

import React, { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Check, ChevronRight, ChevronsUpDown, Loader2 } from "lucide-react"
import { useSchemaPaths } from "@/hooks/use-schema-paths"
import { isExpandableNode } from "@/lib/ats-schema-expansion"
import { cn } from "@/lib/utils"
import type { AtsFieldDescriptor } from "@/types/ats-explorer-api"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

export interface AtsFieldPathPickerProps {
  /** The ATS entity type whose live schema supplies the paths */
  entityType: string
  value: string | null
  onChange: (value: string | null) => void
  placeholder?: string
  id?: string
  /** Keeps only matching leaf paths; association nodes stay so they can be expanded to reach theirs */
  filter?: (field: AtsFieldDescriptor) => boolean
}

/**
 * Searchable picker for raw ATS field paths, populated from the entity's live schema on
 * first open. Association nodes ("jobOrder ▸ JobOrder") expand in place to the associated
 * entity's own fields — including ones the parent metadata never advertises. Free text
 * stays possible via a "use what I typed" item, for JSONPath filters or fields absent
 * from the schema.
 */
export function AtsFieldPathPicker({ entityType, value, onChange, placeholder, id, filter }: AtsFieldPathPickerProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const schema = useSchemaPaths(entityType)

  // Load when the popover opens (and again after an entity change reset the fields)
  useEffect(() => {
    if (open && schema.fields === null && !schema.isLoading) schema.load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, schema.fields, schema.isLoading, entityType])

  const select = (path: string | null) => {
    onChange(path)
    setOpen(false)
    setSearch("")
  }

  const emptyLabel = placeholder ?? t("fieldPathPicker.trigger.placeholder")
  const fields = (schema.fields ?? []).filter((field) => !filter || isExpandableNode(field) || filter(field))
  const showCustomItem = search.trim() !== "" && !fields.some((field) => field.path === search.trim())

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button id={id} variant="outline" role="combobox" className="h-8 w-full justify-between text-xs font-normal">
          <span className={cn("truncate", !value && "text-muted-foreground")}>{value ?? emptyLabel}</span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="start">
        <Command>
          <CommandInput
            placeholder={t("fieldPathPicker.search.placeholder")}
            className="h-8 text-xs"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {schema.isLoading && schema.fields === null ? (
              <div className="flex items-center justify-center py-4 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> {t("fieldPathPicker.state.discovering")}
              </div>
            ) : (
              <>
                <CommandEmpty>{t("fieldPathPicker.state.empty")}</CommandEmpty>
                <CommandGroup>
                  {showCustomItem && (
                    // value={search} always matches the current query, so this stays visible
                    <CommandItem value={search} onSelect={() => select(search.trim())} className="text-xs">
                      {t("fieldPathPicker.actions.useTyped", { value: search.trim() })}
                    </CommandItem>
                  )}
                  {fields.map((field) =>
                    isExpandableNode(field) ? (
                      <ExpandItem
                        key={field.path}
                        field={field}
                        isExpanded={schema.expandedPaths.has(field.path)}
                        isExpanding={schema.expandingPath === field.path}
                        onExpand={() => void schema.expand(field.path)}
                      />
                    ) : (
                      <CommandItem key={field.path} value={field.path} onSelect={() => select(field.path)} className="text-xs">
                        <Check className={cn("mr-2 h-3 w-3", value === field.path ? "opacity-100" : "opacity-0")} />
                        {field.path}
                      </CommandItem>
                    )
                  )}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

interface ExpandItemProps {
  field: AtsFieldDescriptor
  isExpanded: boolean
  isExpanding: boolean
  onExpand: () => void
}

/** An association node: selecting it expands the associated entity's fields under it instead of picking a path */
function ExpandItem({ field, isExpanded, isExpanding, onExpand }: ExpandItemProps) {
  const { t } = useTranslation()

  return (
    <CommandItem
      value={`${field.path} ${field.associatedEntityType}`}
      onSelect={onExpand}
      disabled={isExpanded}
      className="text-xs text-muted-foreground"
    >
      {isExpanding ? (
        <Loader2 className="mr-2 h-3 w-3 animate-spin" />
      ) : (
        <ChevronRight className={cn("mr-2 h-3 w-3 transition-transform", isExpanded && "rotate-90")} />
      )}
      <span className="text-foreground">{field.path}</span>
      <span className="ml-2">{t("fieldPathPicker.items.expandFields", { entity: field.associatedEntityType })}</span>
    </CommandItem>
  )
}
